/**
 * Drizzle query builders + query keys de HORARIO y ASISTENCIA (F3-A).
 *
 * Consumo local-first sobre los streams por rol (my_cohorts_student /
 * my_cohorts_teacher / campus_admin_scope / org_admin_scope): franjas de
 * horario, sesiones de clase, asistencia y alertas de riesgo. La escritura pasa
 * por el write-path (mutations.ts) o por REST (/api/attendance, /api/schedule,
 * /api/cohorts/:id/generate-sessions).
 */

import {
  and,
  asc,
  attendance_record,
  class_session,
  cohort_enrollment,
  eq,
  gte,
  isNull,
  lte,
  type PowerSyncDrizzleDB,
  schedule_slot,
  student_alert,
} from "@space/powersync";

export const scheduleQueryKeys = {
  slotsByCohort: (cohortId: string | undefined) => ["academy-schedule", "slots", cohortId] as const,
  sessionsByCohort: (cohortId: string | undefined) =>
    ["academy-schedule", "sessions", cohortId] as const,
  sessionsBetween: (userId: string | undefined, from: string, to: string) =>
    ["academy-schedule", "my-sessions", userId, from, to] as const,
  attendanceBySession: (sessionId: string | undefined) =>
    ["academy-attendance", "session", sessionId] as const,
  attendanceByCohort: (cohortId: string | undefined) =>
    ["academy-attendance", "cohort", cohortId] as const,
  myAttendance: (userId: string | undefined) => ["academy-attendance", "mine", userId] as const,
  alertsByCohort: (cohortId: string | undefined) => ["academy-alerts", "cohort", cohortId] as const,
  // F3-G: una sesión por id (pantalla de clase en vivo).
  sessionById: (sessionId: string | undefined) =>
    ["academy-schedule", "session", sessionId] as const,
};

// ─── Horario ─────────────────────────────────────────────────────────────────

/** Franjas de horario semanal de una cohorte (ordenadas por día y hora). */
export function getSlotsByCohortQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(schedule_slot)
    .where(and(eq(schedule_slot.cohort_id, cohortId), isNull(schedule_slot.deleted_at)))
    .orderBy(asc(schedule_slot.weekday), asc(schedule_slot.start_time));
}

/** Una sesión de clase por id (F3-G, pantalla de clase en vivo). */
export function getSessionByIdQuery(db: PowerSyncDrizzleDB, sessionId: string) {
  return db
    .select()
    .from(class_session)
    .where(and(eq(class_session.id, sessionId), isNull(class_session.deleted_at)))
    .limit(1);
}

/** Sesiones de clase de una cohorte (por fecha ascendente). */
export function getSessionsByCohortQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(class_session)
    .where(and(eq(class_session.cohort_id, cohortId), isNull(class_session.deleted_at)))
    .orderBy(asc(class_session.date), asc(class_session.start_time));
}

/**
 * MIS sesiones entre dos fechas (para el timetable personal). Cruza las cohortes
 * donde estoy MATRICULADO (my_cohorts_student sincronizó sus class_session); el
 * horario de profesor llega por my_cohorts_teacher (mismas filas class_session).
 */
export function getMySessionsBetweenQuery(db: PowerSyncDrizzleDB, from: string, to: string) {
  return db
    .select()
    .from(class_session)
    .where(
      and(
        isNull(class_session.deleted_at),
        gte(class_session.date, from),
        lte(class_session.date, to),
      ),
    )
    .orderBy(asc(class_session.date), asc(class_session.start_time));
}

// ─── Asistencia ──────────────────────────────────────────────────────────────

/** Asistencia de una sesión (una fila por alumno). */
export function getAttendanceBySessionQuery(db: PowerSyncDrizzleDB, sessionId: string) {
  return db
    .select()
    .from(attendance_record)
    .where(eq(attendance_record.class_session_id, sessionId));
}

/** MI asistencia (filas propias, stream my_cohorts_student). */
export function getMyAttendanceQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(attendance_record).where(eq(attendance_record.user_id, userId));
}

/**
 * Resumen de asistencia por alumno de una cohorte (stats derivadas).
 *
 * **D3 (plan 06 · E3):** sin `leftJoin(user, …)`. Al no haber identidad en la
 * consulta tampoco puede haber `ORDER BY user.name`: el orden por nombre lo
 * aplica `sortByIdentityName` después de hidratar, y aquí se ordena por la
 * matrícula para que la tabla salga estable mientras la identidad viaja.
 */
export function getCohortAttendanceReportQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select({
      user_id: cohort_enrollment.user_id,
      status: cohort_enrollment.status,
      present: cohort_enrollment.sessions_present,
      late: cohort_enrollment.sessions_late,
      absent: cohort_enrollment.sessions_absent,
      excused: cohort_enrollment.sessions_excused,
      absence_streak: cohort_enrollment.absence_streak,
      attendance_rate: cohort_enrollment.attendance_rate,
    })
    .from(cohort_enrollment)
    .where(and(eq(cohort_enrollment.cohort_id, cohortId), isNull(cohort_enrollment.deleted_at)))
    .orderBy(asc(cohort_enrollment.created_at));
}

/** Fila del informe de asistencia SIN identidad. */
export interface AttendanceReportRow {
  user_id: string | null;
  status: string | null;
  present: number | null;
  late: number | null;
  absent: number | null;
  excused: number | null;
  absence_streak: number | null;
  attendance_rate: number | null;
}

// ─── Alertas ─────────────────────────────────────────────────────────────────

/**
 * Alertas de riesgo ABIERTAS de una cohorte (staff).
 *
 * **D3 (plan 06 · E3):** sin `leftJoin(user, …)`. El nombre del alumno de la
 * alerta lo aporta la fuente única de identidad y se pega con `attachIdentity`.
 */
export function getOpenAlertsByCohortQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select({
      id: student_alert.id,
      user_id: student_alert.user_id,
      kind: student_alert.kind,
      payload: student_alert.payload,
      status: student_alert.status,
      created_at: student_alert.created_at,
    })
    .from(student_alert)
    .where(and(eq(student_alert.cohort_id, cohortId), eq(student_alert.status, "open")))
    .orderBy(asc(student_alert.created_at));
}

/** Fila de alerta SIN identidad. */
export interface AlertRow {
  id: string;
  user_id: string | null;
  kind: string | null;
  payload: string | null;
  status: string | null;
  created_at: string | null;
}
